import { Component, forwardRef, inject, Injector, input, model, OnInit, signal, viewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { DxCheckBoxComponent, DxCheckBoxModule } from 'devextreme-angular';
import { ValueChangedEvent } from 'devextreme/ui/check_box';
import { DECORATOR_HOST, DecoratorHost } from '../../../directives/decorator-host';

@Component({
  selector: 'app-checkbox',
  standalone: true,
  imports: [DxCheckBoxModule],
  templateUrl: './checkbox.component.html',
  styleUrl: './checkbox.component.scss',
  host: {
    style: 'align-self: flex-start;',
    class: 'link-component'
  },
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CheckboxComponent), multi: true },
    { provide: DECORATOR_HOST, useExisting: forwardRef(() => CheckboxComponent) }
  ],
})
export class CheckboxComponent implements OnInit, ControlValueAccessor, DecoratorHost {
  private injector = inject(Injector);
  ngControl: NgControl = null;

  readonly value = model<boolean>(false);
  readonly disabled = model<boolean>(false);
  readonly readonly = input<boolean>(false);

  private readonly dx = viewChild.required<DxCheckBoxComponent>('dx');
  private readonly inputId = signal<string | null>(null);

  ngOnInit(): void {
    try {
      this.ngControl = this.injector.get(NgControl);
    }
    catch {
      // No NgControl
    }
  }

  onValueChanged(e: ValueChangedEvent): void {
    const nextValue = this.coerceBoolean(e.value);

    if (nextValue !== this.value()) {
      this.onChange(nextValue);
      this.value.set(nextValue);
    }
  }

  onBlur(_$event: unknown) {
    this.onTouched();
  }

  // Triggered when an external label[for] activates the hidden proxy input
  protected toggleFromLabel(): void {
    if (!this.disabled() && !this.readonly()) {
      const nextValue = !this.value();
      this.value.set(nextValue);
      this.onChange(nextValue);
      this.dx().instance.focus();
    }
  }

  /*
   * ControlValueAccessor interface methods
   */

  writeValue(obj: unknown): void {
    this.value.set(this.coerceBoolean(obj));
  }

  private coerceBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();

      if (normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on') {
        return true;
      }

      if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === 'off' || normalized === '') {
        return false;
      }
    }

    if (typeof value === 'number') {
      return !Number.isNaN(value) && value !== 0;
    }

    if (typeof value === 'bigint') {
      return value !== 0n;
    }

    if (value instanceof Boolean) {
      return value.valueOf();
    }

    if (value !== null && typeof value === 'object' && 'valueOf' in value && typeof value.valueOf === 'function') {
      const primitiveValue = value.valueOf();

      if (primitiveValue !== value) {
        return this.coerceBoolean(primitiveValue);
      }
    }

    return Boolean(value);
  }

  private onChange: (value: unknown) => void = () => { };
  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  onTouched: () => void = () => { };
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(!!isDisabled);
  }


  /*
   * DecoratorHost interface methods
   */

  setInputId(inputId: string | null): void {
    this.inputId.set(inputId);
  }

  getInputId(): string | null {
    return this.inputId();
  }
}
